import { useState } from "react";
import {
  Flex,
  LabeledData,
  OverlayStyle,
  SlideoutDrawerV2,
  Tab,
  Tabs,
  Text,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import {
  DdqProdOpsTaskKind,
  DdqProdOpsTaskState,
  DdqProdOpsWorkflowStatus,
} from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import { formatShortFormDate } from "~/utils/datetime";
import {
  CANCEL_ACTION_LABEL,
  HOLD_ACTION_LABEL,
  RESTART_ACTION_LABEL,
  STATUS_LABELS,
  WORKFLOW_TYPE_LABELS,
} from "../labels";
import { AssignedToPicker } from "./AssignedToPicker";
import { CancelDdqModal } from "./CancelDdqModal";
import { DetailsTab } from "./DetailsTab";
import { HoldModal } from "./HoldModal";
import { LogTab } from "./LogTab";
import * as styles from "./styles_vanilla.css";
import { TasksTab } from "./TasksTab";
import { DrawerTabType, type DdqDrawerProps } from "./types";
import { useProcessTaskActions } from "./useProcessTaskActions";
import { useWorkflowActions } from "./useWorkflowActions";

type OverflowModal = "hold" | "cancel" | null;

export const Drawer = ({ row, isOpen, onClose, onCloseAnimationComplete }: DdqDrawerProps) => {
  const [activeTab, setActiveTab] = useState<DrawerTabType>(DrawerTabType.Tasks);
  const [activeModal, setActiveModal] = useState<OverflowModal>(null);

  const processTask = row?.tasks.find((task) => task.kind === DdqProdOpsTaskKind.Process);
  const {
    onSelectAssignee,
    onUnassign,
    onStart,
    isAssigning,
    isStarting,
    startError,
    assignError,
  } = useProcessTaskActions(row?.targetId ?? "", processTask);
  const {
    onHold,
    onRestart,
    onCancel,
    isHolding,
    isRestarting,
    isCancelling,
    holdError,
    restartError,
    cancelError,
  } = useWorkflowActions(
    row?.id ?? "",
    () => setActiveModal(null),
    () => setActiveModal(null),
  );

  if (!row) {
    return null;
  }

  const subtitles = [row.arena.name, ...row.offerings.map((offering) => offering.name)];
  const isHeld = row.status === DdqProdOpsWorkflowStatus.OnHold;
  const isCancelled = row.status === DdqProdOpsWorkflowStatus.Cancelled;
  const isCompleted = row.status === DdqProdOpsWorkflowStatus.Completed;
  // Restart reopens a held or a cancelled request; completed is the only true dead end.
  const isReopenable = isHeld || isCancelled;
  const isInactive = isReopenable || isCompleted;
  const canStart =
    processTask?.state === DdqProdOpsTaskState.Pending &&
    !processTask.blocked &&
    !!processTask.assignee;

  return (
    <>
      <SlideoutDrawerV2
        isOpen={isOpen}
        onClose={onClose}
        onCloseAnimationComplete={onCloseAnimationComplete}
        isDismissable
        overlayStyle={OverlayStyle.Transparent}
      >
        <SlideoutDrawerV2.ComplexHeader
          title={row.title ?? "Untitled DDQ"}
          description="DDQ request details"
          subtitles={subtitles}
          showBorder={false}
          className={styles.header}
          renderMenu
          menuItems={[
            isReopenable
              ? {
                  label: RESTART_ACTION_LABEL,
                  onClick: onRestart,
                  isDisabled: isRestarting,
                }
              : {
                  label: HOLD_ACTION_LABEL,
                  onClick: () => setActiveModal("hold"),
                  isDisabled: isCompleted,
                },
            {
              label: CANCEL_ACTION_LABEL,
              onClick: () => setActiveModal("cancel"),
              color: Colors.Danger600,
              isDisabled: isCancelled || isCompleted,
            },
          ]}
        />
        <SlideoutDrawerV2.Body className={styles.body}>
          <Flex flexDirection="column" className={styles.container} data-testid="ddq-drawer">
            <Flex className={styles.summary} gap={SPACING.X_LARGE}>
              <LabeledData
                variant="standard"
                flippedLabel
                label="Status"
                value={STATUS_LABELS[row.status]}
                data-testid="ddq-drawer-status"
              />
              <LabeledData
                variant="standard"
                flippedLabel
                label="Request"
                value={WORKFLOW_TYPE_LABELS[row.workflowType]}
                data-testid="ddq-drawer-request-type"
              />
              <LabeledData
                variant="standard"
                flippedLabel
                label="Due date"
                value={row.dueAt ? formatShortFormDate(row.dueAt) : "-"}
                data-testid="ddq-drawer-due-date"
              />
            </Flex>

            <Tabs className={styles.tabs}>
              <Tab
                active={activeTab === DrawerTabType.Tasks}
                text="Tasks"
                onClick={() => setActiveTab(DrawerTabType.Tasks)}
                data-testid="ddq-drawer-tasks-tab"
              />
              <Tab
                active={activeTab === DrawerTabType.Details}
                text="Details"
                onClick={() => setActiveTab(DrawerTabType.Details)}
                data-testid="ddq-drawer-details-tab"
              />
              <Tab
                active={activeTab === DrawerTabType.Log}
                text="Log"
                onClick={() => setActiveTab(DrawerTabType.Log)}
                data-testid="ddq-drawer-log-tab"
              />
            </Tabs>

            <div className={styles.tabContent}>
              {activeTab === DrawerTabType.Tasks && (
                <TasksTab tasks={row.tasks} targetId={row.targetId} isLocked={isInactive} />
              )}
              {activeTab === DrawerTabType.Details && (
                <DetailsTab row={row} isLocked={isInactive} />
              )}
              {activeTab === DrawerTabType.Log && <LogTab workflowId={row.id} />}
            </div>
          </Flex>
        </SlideoutDrawerV2.Body>
        {/* Assignment stays available once started (e.g. reassigning); only Start itself needs Pending. */}
        {processTask && processTask.state !== DdqProdOpsTaskState.Done && !isCompleted && (
          <SlideoutDrawerV2.ComplexFooter
            label={
              <Flex flexDirection="column" gap={SPACING.X_SMALL}>
                <Flex alignItems="center" gap={SPACING.SMALL}>
                  <Text variant="regular-sm" color={Colors.Neutral600}>
                    Assigned to
                  </Text>
                  <AssignedToPicker
                    key={processTask.assignee?.id ?? "unassigned"}
                    currentAssigneeName={processTask.assignee?.fullName ?? undefined}
                    onSelectAssignee={onSelectAssignee}
                    onUnassign={onUnassign}
                    disabled={isAssigning || isStarting || isInactive}
                  />
                </Flex>
                {(startError ?? assignError ?? restartError) && (
                  <Text
                    variant="regular-sm"
                    color={Colors.Danger600}
                    data-testid={
                      startError
                        ? "ddq-drawer-start-error"
                        : assignError
                          ? "ddq-drawer-assign-error"
                          : "ddq-drawer-restart-error"
                    }
                  >
                    {startError ?? assignError ?? restartError}
                  </Text>
                )}
              </Flex>
            }
            primaryButton={
              isReopenable
                ? {
                    label: RESTART_ACTION_LABEL,
                    onClick: onRestart,
                    disabled: isRestarting,
                  }
                : {
                    label: "Start",
                    onClick: onStart,
                    disabled: !canStart || isStarting,
                  }
            }
          />
        )}
      </SlideoutDrawerV2>
      {activeModal === "hold" && (
        <HoldModal
          errorMessage={holdError}
          isSubmitting={isHolding}
          onHide={() => setActiveModal(null)}
          onConfirm={onHold}
        />
      )}
      {activeModal === "cancel" && (
        <CancelDdqModal
          errorMessage={cancelError}
          isSubmitting={isCancelling}
          onHide={() => setActiveModal(null)}
          onConfirm={onCancel}
        />
      )}
    </>
  );
};
